import { useQuery } from "@apollo/client";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { isT2mState } from "encamp-shared/src/constants/t2m-county-lepc-fd/getByState";
import { gql } from "generated-graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import React, { forwardRef, useMemo, useState } from "react";
import { UseControllerReturn } from "react-hook-form";

import { DisplayOnlyField } from "./Forms/DisplayOnlyField";
import { ErrorDisplay } from "./Forms/ErrorDisplay";

type Props = {
  facilityState: string;
  disabled?: boolean;
} & UseControllerReturn<{ county?: string | null }, "county">;

const COUNTIES_QUERY = gql(`
  query Counties($state: String!) {
    counties(state: $state) {
      items
      count
    }
  }
`);

export const CountyPicker: React.FC<Props> = forwardRef(function CountyPicker(
  { field: { value, onChange }, facilityState, fieldState: { error }, disabled },
  ref
) {
  const [inputValue, setInputValue] = useState("");
  const [searchIsOpen, setSearchIsOpen] = useState(false);

  // T2M states require exact county matches, so we disable freeSolo to force dropdown selection
  const isT2m = isT2mState(facilityState);

  const { data, loading, previousData } = useQuery(COUNTIES_QUERY, {
    variables: { state: facilityState },
    skip: !searchIsOpen,
    fetchPolicy: "cache-and-network",
  });

  const options = useMemo(
    () => data?.counties.items ?? previousData?.counties.items ?? [],
    [data?.counties.items, previousData?.counties.items]
  );

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    return <DisplayOnlyField text={value ?? ""} label={"County"} />;
  }

  return (
    <>
      <Autocomplete
        ref={ref}
        freeSolo={!isT2m}
        options={options}
        disabled={disabled}
        noOptionsText="Search for a county"
        onOpen={() => setSearchIsOpen(true)}
        onClose={() => setSearchIsOpen(false)}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        // Only allow onBlur to set arbitrary values for non-T2M states
        onBlur={isT2m ? undefined : (e) => onChange((e.target as HTMLInputElement).value)}
        value={value ?? null}
        onChange={(_, val) => onChange(val)}
        getOptionLabel={(option: string) => option}
        renderOption={(props, option) => <li {...props}>{option}</li>}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              label="County"
              variant="outlined"
              autoComplete="off"
              inputProps={{
                ...params.inputProps,
                autoComplete: "off",
                "data-lpignore": "true",
                "data-form-type": "other",
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
            <ErrorDisplay error={error} />
          </>
        )}
      />
    </>
  );
});
